import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, LogOut, Menu, X, ChevronDown } from "lucide-react";
import { AvatarBadge } from "@/components/AvatarBadge";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { login, scrollToId } from "@/lib/api";
import { useNavigate, useLocation } from "react-router-dom";

const LINKS = [
  ["shop", "Bracelets"],
  ["design", "Design yours"],
  ["thoughts", "Thoughts"],
  ["reels", "Reels"],
  ["danganronpa", "Danganronpa"],
  ["apothecary", "Apothecary Diaries"],
  ["witchhat", "Witch Hat Atelier"],
  ["villainess", "Inept Villainess"],
  ["mdzs", "Master of Diabolism"],
  ["about", "The Hollow"],
];

const FANDOM_IDS = ["danganronpa", "apothecary", "witchhat", "villainess", "mdzs"];
const MAIN_LINKS = LINKS.filter(([id]) => !FANDOM_IDS.includes(id));
const FANDOM_LINKS = LINKS.filter(([id]) => FANDOM_IDS.includes(id));
const linkCls = "relative font-mono text-xs uppercase tracking-[0.25em] text-purple-100 hover:text-white group whitespace-nowrap";

const FandomMenu = ({ go }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button data-testid="nav-fandoms-btn" onClick={() => setOpen((o) => !o)} className={`${linkCls} inline-flex items-center gap-1`}>
        Fan pages <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3" data-testid="nav-fandoms-menu">
          <div className="rounded-2xl border border-purple-300/20 bg-[#170a24]/95 backdrop-blur-xl p-2 min-w-[220px] shadow-2xl">
            {FANDOM_LINKS.map(([id, label]) => (
              <button key={id} data-testid={`nav-link-${id}`} onClick={() => { setOpen(false); go(id); }}
                className="block w-full text-left rounded-xl px-4 py-2.5 text-sm text-purple-50 hover:bg-purple-300/15 transition-colors">
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AuthButton = () => {
  const { user, loading, logout, setAvatarOpen } = useAuth();
  if (loading) return <span className="w-9 h-9" />;
  if (!user)
    return (
      <button data-testid="nav-google-login-btn" onClick={login}
        className="rounded-full border border-purple-300/40 px-3 md:px-4 py-2 text-sm whitespace-nowrap hover:bg-purple-300 hover:text-[#0e0616] transition-colors duration-300">
        Sign in
      </button>
    );
  return (
    <div className="flex items-center gap-2" data-testid="nav-user">
      <button onClick={() => setAvatarOpen(true)} data-testid="nav-avatar-btn" aria-label="Edit my avatar" title="Edit my avatar"
        className="rounded-full transition-transform hover:scale-105">
        {user.avatar ? <AvatarBadge avatar={user.avatar} size={34} testid="nav-avatar" /> :
          user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-1 ring-purple-300/50" /> :
          <span className="w-8 h-8 rounded-full bg-purple-400/30 grid place-items-center text-sm">{user.name?.[0]}</span>}
      </button>
      <button data-testid="nav-logout-btn" onClick={logout} aria-label="Sign out" className="p-2 text-purple-200 hover:text-white transition-colors">
        <LogOut className="w-4 h-4" />
      </button>
    </div>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const go = (id) => {
    setMenu(false);
    if (id === "thoughts") { nav("/thoughts"); return; }
    if (id === "reels") { nav("/reels"); return; }
    if (id === "danganronpa") { nav("/danganronpa"); return; }
    if (id === "apothecary") { nav("/apothecary-diaries"); return; }
    if (id === "witchhat") { nav("/witch-hat-atelier"); return; }
    if (id === "villainess") { nav("/inept-villainess"); return; }
    if (id === "mdzs") { nav("/master-of-diabolism"); return; }
    if (loc.pathname !== "/") { nav("/"); setTimeout(() => scrollToId(id), 400); }
    else scrollToId(id);
  };

  return (
    <motion.header initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#170a24]/80 border-b border-purple-400/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Logo onClick={() => go("top")} />
        <nav className="hidden xl:flex items-center gap-7">
          {MAIN_LINKS.slice(0, 4).map(([id, label]) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)} className={linkCls}>
              {label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-amber-200 group-hover:w-full transition-[width] duration-500" />
            </button>
          ))}
          <FandomMenu go={go} />
          {MAIN_LINKS.slice(4).map(([id, label]) => (
            <button key={id} data-testid={`nav-link-${id}`} onClick={() => go(id)} className={linkCls}>
              {label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-amber-200 group-hover:w-full transition-[width] duration-500" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 md:gap-3">
          <AuthButton />
          <button data-testid="nav-cart-btn" onClick={() => setOpen(true)} aria-label="Open cart"
            className="relative p-2.5 rounded-full hover:bg-purple-400/15 transition-colors">
            <ShoppingBag className="w-5 h-5" />
            <AnimatePresence>
              {count > 0 && (
                <motion.span key={count} initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} data-testid="nav-cart-count"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-200 text-[#0e0616] text-[11px] font-semibold grid place-items-center">
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button className="xl:hidden p-2" data-testid="nav-mobile-menu-btn" onClick={() => setMenu((m) => !m)} aria-label="Menu">
            {menu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="xl:hidden overflow-hidden border-t border-purple-400/10">
            {LINKS.map(([id, label]) => (
              <button key={id} data-testid={`mobile-nav-link-${id}`} onClick={() => go(id)}
                className="block w-full text-left px-6 py-4 font-display text-2xl">{label}</button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
